import * as math from 'mathjs';
import Startposen from "./Startposen";
import Fkin from "./Fkin";
import DiffCartesianC1 from "./DiffCartesianC1";
import CheckAR from "./CheckAR";
import MakeAuswahl from "./MakeAuswahl";

class IkinPtpC2 {
    /**
     * General RTRAFO for PTP command with cartesian target specification
     * Uses genetic algorithm approach with generations
     * 
     * @param {Matrix} F - Target pose (4x4 or 3x4 frame)
     * @param {Array|Matrix} q - Initial joint angles
     * @param {Object} r - Robot object with joint properties
     * @param {Array} mask - Mask vector [mask_pos, mask_ori] for position and orientation
     * @param {number} tIpo - Time step (not used in this implementation)
     * @returns {Array} [qs, status] - qs: array of solution joint angles, status: 'ok' or 'out of range'
     */
    run(F, q, r, mask, tIpo) {
        let startposen = new Startposen();
        let fkin = new Fkin();
        let diffCartesianC1 = new DiffCartesianC1();
        let checkAR = new CheckAR();
        let makeAuswahl = new MakeAuswahl();

        // nw=2: hier werden nw Zwischenwerte qzw(i,k) von qi im gleichen Abstand berechnet
        let nw = 2;
        
        // Vorbesetzungen
        let q_alt = [...math.squeeze(q).toArray ? math.squeeze(q).toArray() : math.squeeze(q)]; // Sicherung Übergabevektor
        let qs = []; // in diesem Vektor werden die Lösungen für die Gelenkoordinaten gespeichert
        
        let N = r.jointAmount; // N: Anzahl der Gelenke
        let status = 'ok'; // Vorbesetzung
        
        let qmin = r.joints.map(j => j.qmin);
        let qmax = r.joints.map(j => j.qmax);
        
        let F_akt = F; // aktuelle Zielpose
        
        // Winkelbereich bzw. Streckenbereich zwischen q-min und q_max
        let q_distanz = qmax.map((qmax_i, i) => qmax_i - qmin[i]);
        let fak_min = 1/10000; // 0.01% von q_distanz
        let fak_max = 0.3; // 30% von q_distanz
        
        // Längenmaße etc
        let hh = r.joints.map(j => j.h); // h_gel(i)=1: rotat. Gelenk, h_gel(i)=0: translat. Gelenk
        
        // mask Vektor
        let mask_pos = mask.slice(0, 3); // um z. B. bei RR und mask=[1 0 0 1 0 0] als x und A den Abstand position nur für x zu bewerten
        
        let l_gen_max = 80;
        
        // Ermittlung aller Startposen
        // nw=2: auf jeder qi-Achse werden nw Zwischenwerte gewählt (qimax und qimin gehören nicht dazu)
        // insgesamt gibt es dann nw^N Startvektoren
        let [qstart, lges] = startposen.run(N, nw, qmin, qmax);
        // lges: Zahl der Startgelenkvektoren
        let zahlposen = 1; // es gibt bei ikin-PTP_GA nur eine Zielpose kartesisch
        
        // Genauigkeitsmaß ab_min
        let k_pos = 0.7; // Gewichtung der Genauigkeit für Position
        let k_ori = 0.3; // Gew. der Genauigkeit für Orientierung
        
        // Wenn kein kartesischer Orientierungsfreiheitsgrad vorhanden, dann k_pos=1, k_ori=0
        let k_sum_ori = mask.slice(3, 6).reduce((a, b) => a + b, 0);
        if (k_sum_ori === 0) {
            k_pos = 1;
            k_ori = 0;
        }
        
        // Wenn der Wert von des aktuellen Abstandsmaßes ab unter ab_min sinkt, wird das Verfahren als erfolgreich beendet abgebrochen.
        let ab_pos_min = 0.5 * 1e-4; // 0.05mm
        let ab_ori_min = 0.1 * Math.PI / 180; // (0.1°)
        let ab_min = ab_pos_min * k_pos + ab_ori_min * k_ori;
        
        let lposen = Math.pow(3, N); // vorläufig, Breite der generierten Posen als nächste Generation
        let abstand = math.ones(lposen, 1); // Abstandsmaß (der Population), Startwert
        
        // 3*4 or 4*4 Zielframe - ensure it's 4x4 if it's 3x4
        let TW_z = F_akt; // aktuelle Zielpose
        // Ensure TW_z is 4x4 for DiffCartesianC1 (it might be 3x4)
        if (TW_z && TW_z.size && TW_z.size().length >= 2 && TW_z.size()[0] === 3) {
            // Convert 3x4 to 4x4 by adding [0, 0, 0, 1] as last row
            let last_row = math.matrix([[0, 0, 0, 1]]);
            TW_z = math.concat(TW_z, last_row, 0);
        }
        
        let kf_0 = 1;
        let kf = kf_0; // Gewichtung für dq
        let kf_red = 0.8; // Reduktionsfaktor von kf
        
        // Berechnung von ab_max, maximales Abstandsmass (überschlägig)
        let d_betrag_max = [];
        let alink = [];
        for (let i1 = 0; i1 < N; i1++) {
            d_betrag_max[i1] = Math.max(Math.abs(qmin[i1]), Math.abs(qmax[i1]));
            let a_val = r.joints[i1].a || 0;
            let d_val = r.joints[i1].d || 0;
            alink[i1] = Math.sqrt(a_val * a_val + hh[i1] * d_val * d_val + (1 - hh[i1]) * d_betrag_max[i1] * d_betrag_max[i1]);
        }
        let ab_pos_max = 2 * alink.reduce((a, b) => a + b, 0); // *2 wegen unten - oben
        let ab_ori_max = Math.PI;
        let ab_max = k_pos * ab_pos_max + k_ori * ab_ori_max;
        
        let q_zw = math.zeros(N, lposen); // Startwerte für die Zwischenvektoren in der jeweiligen Generation
        let auswahl = math.zeros(N, lposen); // Startwerte
        
        let kd = math.zeros(lges, 1); // Startwerte für Zähler zur Reduktion von kf für jeden Startvektor
        
        // Berechnung von auswahl using make_auswahl
        // This creates 3^N combinations in the correct order (outer loop changes slowest)
        auswahl = makeAuswahl.run(N);
        
        let qloes = math.zeros(N, lges);
        let gen_schritte = math.zeros(lges, 1); // Generationen bei jedem Startvektor
        
        //***************************************************************************************************
        // Schleife über alle Startvektoren auf Gelenkebene
        //******************************************************************************************
        for (let lauf = 0; lauf < lges; lauf++) {
            let q_stamm = math.subset(qstart, math.index(math.range(0, N), lauf)); // aktueller Startvektor
            q_stamm = math.squeeze(q_stamm);
            q_stamm = [...(q_stamm.toArray ? q_stamm.toArray() : q_stamm)]; // Convert to array
            let q = [...q_stamm];
            let l_gen = 1; // Startwert (1. Generation)
            
            kf = kf_0;
            // Abstandsmass ab_0 zwischen Startframe und Zielframe
            let [TW_0, T0] = fkin.run(r, q);
            // Extract the final 4x4 frame from T0 (4x4xN -> 4x4)
            let TW_0_frame = math.subset(T0, math.index(math.range(0, T0.size()[0]), math.range(0, T0.size()[1]), N - 1));
            TW_0_frame = math.squeeze(TW_0_frame);
            let TW_Start = math.subset(TW_0_frame, math.index([0, 1, 2], math.range(0, 4))); // 3*4 Frame
            
            // Berechnung von ab_0, Abstandsmass zu Beginn
            // Use the 4x4 frame (or 3x4) for comparison
            let [delta_p_0, phi_0] = diffCartesianC1.run(TW_0_frame, TW_z);
            let delta_p_0_arr = math.squeeze(delta_p_0).toArray ? math.squeeze(delta_p_0).toArray() : math.squeeze(delta_p_0);
            // Ensure delta_p_0_arr is an array
            if (!Array.isArray(delta_p_0_arr)) {
                delta_p_0_arr = [delta_p_0_arr];
            }
            // Apply mask: norm(delta_p .* mask_pos')
            let masked_delta = delta_p_0_arr.map((val, i) => (i < mask_pos.length ? val * mask_pos[i] : val));
            let ab_0 = math.norm(masked_delta) * k_pos + Math.abs(phi_0) * k_ori;
            let ab = ab_0; // Startwert
            let ab_alt = ab_0; // zum Start
            
            // Berechnung des Startwertes dq_0 für den speziellen Startvektor
            let dq_0 = q_distanz.map(qd => (fak_min + (fak_max - fak_min) * (ab_0 / ab_max)) * qd);
            let dq = [...dq_0]; // Startwert
            
            //--------------------------------------------------------------------------------------------------
            //***************************************************************************************************
            // Schleife über alle Stufen/Generationen
            //******************************************************************************************
            while (l_gen <= l_gen_max && ab > ab_min) {
                for (let lz = 0; lz < lposen; lz++) { // Schleife über alle potentiellen Nachfolgerposen eines q_stamm
                    let auswahl_col = math.subset(auswahl, math.index(math.range(0, N), lz));
                    auswahl_col = math.squeeze(auswahl_col);
                    let auswahl_arr_col = auswahl_col.toArray ? auswahl_col.toArray() : auswahl_col;
                    
                    // q_zw(:,lz) = q_stamm + auswahl(:,lz).*dq
                    let q_zw_col = q_stamm.map((q_stamm_i, i) => q_stamm_i + auswahl_arr_col[i] * dq[i]);
                    // Assign each element individually to avoid dimension mismatch
                    for (let i = 0; i < N; i++) {
                        q_zw = math.subset(q_zw, math.index(i, lz), q_zw_col[i]);
                    }
                    
                    // [T, T0] = fkin(r, q_zw(:,lz))
                    let [T, T0] = fkin.run(r, q_zw_col);
                    // Extract the final 4x4 frame from T0 (4x4xN -> 4x4)
                    let TW_dim4 = math.subset(T0, math.index(math.range(0, T0.size()[0]), math.range(0, T0.size()[1]), N - 1));
                    TW_dim4 = math.squeeze(TW_dim4);
                    // Use the full 4x4 frame for DiffCartesianC1 (it expects 4x4 or 3x4)
                    let TW = TW_dim4;
                    
                    // [delta_p, phi] = diff_cartesian_c1(TW(:,:,lz), TW_z)
                    let [delta_p, phi] = diffCartesianC1.run(TW, TW_z);
                    let delta_p_arr = math.squeeze(delta_p).toArray ? math.squeeze(delta_p).toArray() : math.squeeze(delta_p);
                    // Ensure delta_p_arr is an array
                    if (!Array.isArray(delta_p_arr)) {
                        delta_p_arr = [delta_p_arr];
                    }
                    // Apply mask: norm(delta_p .* mask_pos')
                    let masked_delta = delta_p_arr.map((val, i) => (i < mask_pos.length ? val * mask_pos[i] : val));
                    
                    // abstand(lz) = norm(delta_p .* mask_pos') * k_pos + abs(phi) * k_ori
                    abstand = math.subset(abstand, math.index(lz, 0), 
                        math.norm(masked_delta) * k_pos + Math.abs(phi) * k_ori);
                }
                
                // [Ax, Ind_ab] = sort(abstand) - die Abstände zur Zielpose aufsteigend sortiert
                let abstand_arr = [];
                for (let i = 0; i < lposen; i++) {
                    abstand_arr.push({val: math.subset(abstand, math.index(i, 0)), idx: i});
                }
                abstand_arr.sort((a, b) => a.val - b.val);
                let Ax = abstand_arr.map(a => a.val);
                let Ind_ab = abstand_arr.map(a => a.idx);
                
                if (Ax[0] - ab_alt >= 0) {
                    // ab hat sich nicht verändert bzw. verschlechtert
                    kf = kf * kf_red;
                    dq = dq.map(dq_i => kf * dq_i);
                    let k_fortschritt = 0;
                    kd = math.subset(kd, math.index(lauf, 0), math.subset(kd, math.index(lauf, 0)) + 1);
                } else {
                    // hier Veränderung in ab
                    ab = Ax[0]; // hier hat sich ab reduziert
                    let k_fortschritt = 1;
                    q_stamm = math.subset(q_zw, math.index(math.range(0, N), Ind_ab[0]));
                    q_stamm = math.squeeze(q_stamm);
                    q_stamm = q_stamm.toArray ? q_stamm.toArray() : q_stamm;
                    
                    let ly = 1; // Startwert
                    let out_range = 1; // Startwert: nicht im Bewegungsbereich
                    while (ly <= lposen && out_range === 1) {
                        // über alle Posen wird eine Lösung gesucht, die im Bewegungsbereich liegt
                        q = math.subset(q_zw, math.index(math.range(0, N), Ind_ab[ly - 1]));
                        q = math.squeeze(q);
                        q = [...(q.toArray ? q.toArray() : q)];
                        out_range = 0; // Startwert
                        
                        out_range = checkAR.run(q, qmin, qmax, q_alt, N, hh);
                        ly = ly + 1;
                    }
                    
                    if (ly <= lposen && Ax[ly - 1] < ab_alt) {
                        ab = Ax[ly - 1];
                        q_stamm = [...q]; // neuer Stammvektor
                        let k_fortschritt = 1;
                    } else {
                        // alle Lösungen dieser Stufe nicht im Bewegungsbereich oder es wird keine Verbesserung erzielt
                        ab = ab_alt; // ab = Ax(ly) nicht möglich, da q nicht im Bewegungsbereich
                        kf = kf * kf_red;
                        dq = dq.map(dq_i => kf * dq_i);
                        let k_fortschritt = 0;
                        kd = math.subset(kd, math.index(lauf, 0), math.subset(kd, math.index(lauf, 0)) + 1);
                    }
                }
                
                l_gen = l_gen + 1;
                ab_alt = ab;
            } // für while Schleife
            
            // Assign q_stamm to qloes column by column
            // q_stamm is an array, so assign each element individually
            for (let i = 0; i < N; i++) {
                qloes = math.subset(qloes, math.index(i, lauf), q_stamm[i]);
            }
            gen_schritte = math.subset(gen_schritte, math.index(lauf, 0), l_gen - 1);
        } // für Schleife über alle Startvektoren
        
        // Es werden nur Lösungen genommen, die im Arbeitsbereich liegen und die Genauigkeitsanforderungen erfüllen.
        // Das ist bei denen Lösungen der Fall bei denen l_gen unter l_gen_max blieb.
        let qloes_red = [];
        let zahl_red = 0; // Zahl der reduzierten Lösungen (im AR, genügend genau)
        
        for (let i1 = 0; i1 < lges; i1++) {
            let gen_val = math.subset(gen_schritte, math.index(i1, 0));
            gen_val = typeof gen_val === 'number' ? gen_val : (gen_val && gen_val.valueOf ? gen_val.valueOf() : parseFloat(gen_val));
            if (gen_val < l_gen_max) {
                let qloes_col = math.subset(qloes, math.index(math.range(0, N), i1));
                qloes_col = math.squeeze(qloes_col);
                let qloes_arr = [...(qloes_col.toArray ? qloes_col.toArray() : qloes_col)];
                
                // Make a copy of qloes_arr for CheckAR (it modifies in place but we need the original)
                let qloes_arr_copy = [...qloes_arr];
                let out_range = checkAR.run(qloes_arr_copy, qmin, qmax, q_alt, N, hh);
                if (out_range === 0) {
                    zahl_red = zahl_red + 1;
                    qloes_red.push(qloes_arr); // Push the array directly
                }
            }
        }
        
        let abw = math.zeros(zahl_red, 1);
        
        if (zahl_red >= 1) {
            for (let l1 = 0; l1 < zahl_red; l1++) {
                // abw(l1,1) = norm(q_alt - qloes_red(:,l1))
                let diff = q_alt.map((q_alt_i, i) => q_alt_i - qloes_red[l1][i]);
                abw = math.subset(abw, math.index(l1, 0), math.norm(diff));
            }
            
            // [Abq, Ind_abw] = sort(abw) - die Abstände der gültigen Lösung zu q_alt werden aufsteigend sortiert
            let abw_arr = [];
            for (let i = 0; i < zahl_red; i++) {
                abw_arr.push({val: math.subset(abw, math.index(i, 0)), idx: i});
            }
            abw_arr.sort((a, b) => a.val - b.val);
            let Ind_abw = abw_arr.map(a => a.idx);
            
            qs = qloes_red[Ind_abw[0]];
        } else {
            status = 'out of range';
            qs = []; // Return empty array if no solution found
        }
        
        // Ensure qs is an array
        if (!qs || !Array.isArray(qs)) {
            qs = [];
        }
        
        return [qs, status];
    }
}

export default IkinPtpC2;

